import { Injectable } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { TranslateService } from '@ngx-translate/core'

@Injectable({
  providedIn: 'root',
})
export class BrowserTabProgressService {
  private originalTitle: string | null = null
  private originalFavicon: string | null = null

  private canvas: HTMLCanvasElement | null = null
  private readonly isRetina = window.devicePixelRatio > 1
  private readonly color = '#0899ab'
  private readonly completeColor = '#66A10D'
  private readonly background = '#e5e7eb'
  private readonly shadow = '#ffffff'

  public constructor(
    private readonly translateService: TranslateService,
    private readonly titleService: Title
  ) {}

  public update(percentage: number): void {
    if (!this.originalTitle) {
      this.originalTitle = this.titleService.getTitle()
    }
    if (!this.originalFavicon) {
      const tag = this.getFaviconTag()
      this.originalFavicon = tag
        ? (tag.getAttribute('href') ?? '/favicon.ico')
        : '/favicon.ico'
    }

    if (Number.isNaN(percentage) || !Number.isFinite(percentage)) {
      return
    }

    this.titleService.setTitle(
      `${percentage}% ` +
        this.translateService.instant(
          'pages.uploadFile.steps.progress.browserTitle'
        )
    )

    this.drawFavicon(percentage)
  }

  public reset(): void {
    if (this.originalTitle) {
      this.titleService.setTitle(this.originalTitle)
    }
    if (this.originalFavicon) {
      this.setFaviconTag(this.originalFavicon)
      this.originalFavicon = null
    }
  }

  private getCanvas(): HTMLCanvasElement {
    if (!this.canvas) {
      this.canvas = document.createElement('canvas')
      const size = this.isRetina ? 32 : 16
      this.canvas.width = size
      this.canvas.height = size
    }
    return this.canvas
  }

  private drawFavicon(percentage: number): void {
    const canvas = this.getCanvas()
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      return
    }

    const w = canvas.width
    const h = canvas.height
    const center = w / 2
    const radius = Math.min(w / 2, h / 2)

    ctx.clearRect(0, 0, w, h)

    // Schatten (äußerer Kreis)
    ctx.beginPath()
    ctx.moveTo(center, center)
    ctx.arc(center, center, radius, 0, Math.PI * 2, false)
    ctx.fillStyle = this.shadow
    ctx.fill()

    // Hintergrundkreis
    ctx.beginPath()
    ctx.moveTo(center, center)
    ctx.arc(center, center, radius - 2, 0, Math.PI * 2, false)
    ctx.fillStyle = this.background
    ctx.fill()

    // Fortschritts-Segment (Pie)
    if (percentage > 0) {
      ctx.beginPath()
      ctx.moveTo(center, center)
      ctx.arc(
        center,
        center,
        radius - 2,
        -0.5 * Math.PI,
        (-0.5 + (2 * percentage) / 100) * Math.PI,
        false
      )
      ctx.lineTo(center, center)
      ctx.fillStyle = percentage >= 100 ? this.completeColor : this.color
      ctx.fill()
    }

    this.setFaviconTag(canvas.toDataURL())
  }

  private getFaviconTag(): HTMLLinkElement | null {
    const links = document.getElementsByTagName('link')
    for (let i = 0; i < links.length; i++) {
      const rel = links[i].getAttribute('rel')
      if (rel === 'icon' || rel === 'shortcut icon') {
        return links[i]
      }
    }
    return null
  }

  private removeFaviconTag(): void {
    const links = Array.from(document.getElementsByTagName('link'))
    for (const link of links) {
      const rel = link.getAttribute('rel')
      if (rel === 'icon' || rel === 'shortcut icon') {
        link.remove()
      }
    }
  }

  private setFaviconTag(url: string): void {
    this.removeFaviconTag()
    const link = document.createElement('link')
    link.type = 'image/x-icon'
    link.rel = 'icon'
    link.href = url
    document.getElementsByTagName('head')[0].appendChild(link)
  }
}
